import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigationAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of } from 'rxjs';

import { BaseEffects } from '../../../base.effects';
import { selectCompany } from '../../company/company-state.selectors';
import { NotificationActions } from '../../notifications/notification.actions';
import { AuthActions } from '../auth.actions';
import { AuthStateSelectors } from '../auth.selectors';
import { PhoneNumberActions } from '../change-phone-number/change-phone-number.actions';
import { AmplifyService } from '../services/amplify.service';

import { UserAccountActions } from './user-account.actions';

@Injectable()
export class UserAccountEffects extends BaseEffects {
  cognitoService = inject(AmplifyService);
  store = inject(Store);
  http = inject(HttpClient);
  router = inject(Router);

  accountPageRouted = this.mapEffect({
    action: routerNavigationAction,
    filterFn: action => action.payload.routerState.url.toLowerCase().includes('/settings/user/account'),
    mapFn: () => AuthActions.getCognitoUserAttributes(),
  });

  getCognitoUserAttributes$ = this.switchMapEffect({
    action: AuthActions.getCognitoUserAttributes,
    switchMapFn: () =>
      this.cognitoService.getUserAttributes().pipe(
        map(attributes => AuthActions.setCognitoUserAttributes({ attributes })),
        catchError((error: unknown) => of(AuthActions.getAttributesFailed({ error })))
      ),
  });

  getCognitoUserAttributes_mfa$ = this.switchMapEffect({
    action: AuthActions.getCognitoUserAttributes,
    switchMapFn: () =>
      this.cognitoService.fetchMfaPreference().pipe(
        map(mfaPreference => AuthActions.setCognitoMfaAttributes({ mfaPreference })),
        catchError((error: unknown) => of(AuthActions.getAttributesFailed({ error })))
      ),
  });

  configureMFA$ = this.switchMapEffect({
    action: UserAccountActions.configureMFA,
    switchMapFn: () =>
      this.cognitoService.getUserAttributes().pipe(
        map(attributes => {
          if (attributes.phone_number_verified === 'true') return UserAccountActions.enableMFA();
          if (attributes.phone_number) {
            return PhoneNumberActions.sendVerification({ workflow: 'ENABLE_MFA', isResend: false });
          }
          return NotificationActions.showSnackbar({
            message: 'Please enter a valid phone number and verify it to enable MFA',
          });
        }),
        catchError((error: unknown) => of(UserAccountActions.enableMFAFailure({ error })))
      ),
  });

  confirmationCodeConfirmed$ = this.mapEffect({
    action: PhoneNumberActions.confirmationCodeConfirmed,
    filterFn: ({ workflow }) => workflow === 'ENABLE_MFA',
    mapFn: () => UserAccountActions.configureMFA(),
  });

  enableSmsMfa$ = this.switchMapEffect({
    action: UserAccountActions.enableMFA,
    switchMapFn: () =>
      this.cognitoService.enableSmsMfa().pipe(
        map(() => UserAccountActions.enableMFASuccess()),
        catchError((error: unknown) => of(UserAccountActions.enableMFAFailure({ error })))
      ),
  });

  // Not sure disable is what we desire here; probably s/b NOT_PREFERRED
  disableMfa$ = this.switchMapWithConcatEffect({
    action: UserAccountActions.disableMFA,
    concatLatestFromFn: () => this.store.select(AuthStateSelectors.selectHasNonSmsMfaEnabled),
    switchMapFn: (_, hasRemainingMfaEnabled) =>
      this.cognitoService.disableSms().pipe(
        map(() => UserAccountActions.disableMFASuccess({ hasRemainingMfaEnabled })),
        catchError((error: unknown) => of(UserAccountActions.disableMFAFailure({ error })))
      ),
  });

  disableMFASuccess$ = this.mapEffect({
    action: UserAccountActions.disableMFASuccess,
    mapFn: () => NotificationActions.showSnackbar({ message: 'SMS MFA disabled' }),
  });

  disableMFASuccess_companyIsMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountActions.disableMFASuccess),
      concatLatestFrom(() => this.store.select(selectCompany)),
      filter(([{ hasRemainingMfaEnabled }, company]) => company.settings.requireMfa && !hasRemainingMfaEnabled),
      map(() =>
        NotificationActions.showError({
          message: 'Please enable MFA for full access to this company',
          title: 'MFA Setup Required',
        })
      )
    )
  );

  enableMFASuccess$ = this.mapEffect({
    action: UserAccountActions.enableMFASuccess,
    mapFn: () => NotificationActions.showSnackbar({ message: 'SMS MFA enabled' }),
  });

  refreshCognitoMfa = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccountActions.enableMFASuccess, UserAccountActions.disableMFASuccess),
      map(() => AuthActions.getCognitoUserAttributes())
    )
  );

  navigateToCreateNewCompany$ = this.tapWithConcatEffect({
    action: UserAccountActions.createNewCompany,
    concatLatestFromFn: () => this.store.select(selectCompany),
    tapFn: (_, company) => {
      const affiliateParam = company.affiliateCode ? `?affiliate=${company.affiliateCode}` : '';
      this.router.navigateByUrl(`/create-company${affiliateParam}`);
    },
  });
}
